import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { api, mutate } from '../shared/api';
import { adminSectionDefinition, adminSectionNavLabel } from '../shared/adminSectionRegistry';
import { matchesQuickSearch, QuickSearchInput } from '../shared/quickSearch';

type BootstrapPayload = {
    companies: Array<{ id: number; code: string; name: string }>;
    persons: Array<{ id: number; company_id: number; display_name: string; default_department_id: number | null }>;
    projects: Array<{ id: number; company_id: number; code: string; name: string }>;
    departments: Array<{ id: number; company_id: number; code: string; name: string; sector_id: number | null; kanal: string | null }>;
    sectors: Array<{ id: number; company_id: number; code: string; name_hr: string; name_en: string }>;
};

type ThreadPayload = {
    id: number;
    public_code: string;
    company: { id: number; code: string; name: string } | null;
    sector: { id: number; code: string; name_hr: string } | null;
    department: { id: number; code: string; name: string; kanal?: string | null } | null;
    cost_place_node: { id: number; code: string; name_hr: string; depth: number } | null;
    responsible_person: { id: number; display_name: string } | null;
    project: { id: number; code: string; name: string } | null;
    canonical_text: string | null;
};

export function AdminPurchaseOrderCodesSection({
    token,
    locale,
    reloadTick,
}: {
    token: string;
    locale: 'hr' | 'en';
    reloadTick: number;
}) {
    const def = adminSectionDefinition('125');
    const title = def ? adminSectionNavLabel(def, locale) : '125';

    const [bootstrap, setBootstrap] = useState<BootstrapPayload | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [companyId, setCompanyId] = useState('');
    const [personId, setPersonId] = useState('');
    const [projectId, setProjectId] = useState('');
    const [sectorId, setSectorId] = useState('');
    const [kanalVal, setKanalVal] = useState('');
    const [departmentId, setDepartmentId] = useState('');
    const [saving, setSaving] = useState(false);
    const [lastPublicCode, setLastPublicCode] = useState('');
    const [lastThread, setLastThread] = useState<ThreadPayload | null>(null);

    const [resolveCompanyId, setResolveCompanyId] = useState('');
    const [resolveCode, setResolveCode] = useState('');
    const [resolvedThread, setResolvedThread] = useState<ThreadPayload | null>(null);
    const [pickerSearch, setPickerSearch] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await api<BootstrapPayload>('/admin/purchase-order-codes/bootstrap', token);
            setBootstrap(res);
            // Default: prva firma koja IMA osobe ili projekt (bolji UX nego ostalo na prvoj po abecedi).
            const firstWithData = res.companies.find((c) =>
                res.persons.some((p) => Number(p.company_id) === Number(c.id)) || res.projects.some((p) => Number(p.company_id) === Number(c.id)),
            );
            const defaultCompanyId = String((firstWithData ?? res.companies[0])?.id ?? '');
            setCompanyId((c) => c || defaultCompanyId);
            setResolveCompanyId((c) => c || defaultCompanyId);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'API');
            setBootstrap(null);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        void load();
    }, [load, reloadTick]);

    const companies = bootstrap?.companies ?? [];
    const cid = companyId ? Number(companyId) : 0;

    const persons = bootstrap?.persons.filter((p) => Number(p.company_id) === cid) ?? [];
    const projects = bootstrap?.projects.filter((p) => Number(p.company_id) === cid) ?? [];
    const sectors = bootstrap?.sectors?.filter((s) => Number(s.company_id) === cid) ?? [];
    const kanali = Array.from(new Set((bootstrap?.departments ?? []).filter((d) => Number(d.company_id) === cid).map((d) => d.kanal).filter((k): k is string => !!k)));
    const departments = bootstrap?.departments.filter((d) => Number(d.company_id) === cid && (!sectorId || String(d.sector_id ?? '') === sectorId) && (!kanalVal || d.kanal === kanalVal)) ?? [];

    const personsPick = useMemo(() => {
        const q = pickerSearch.trim();
        if (!q) {
            return persons;
        }

        return persons.filter((p) => matchesQuickSearch([p.id, p.display_name], q));
    }, [persons, pickerSearch]);

    const projectsPick = useMemo(() => {
        const q = pickerSearch.trim();
        if (!q) {
            return projects;
        }

        return projects.filter((p) => matchesQuickSearch([p.id, p.code, p.name], q));
    }, [projects, pickerSearch]);

    const departmentsPick = useMemo(() => {
        const q = pickerSearch.trim();
        if (!q) {
            return departments;
        }

        return departments.filter((d) => matchesQuickSearch([d.id, d.code, d.name], q));
    }, [departments, pickerSearch]);

    const departmentsForProject = departmentsPick;

    async function submitMinimal(e: FormEvent) {
        e.preventDefault();
        if (!personId || !projectId || saving) {
            return;
        }
        setSaving(true);
        setError('');
        try {
            const body: Record<string, unknown> = {
                responsible_user_id: Number(personId),
                project_id: Number(projectId),
            };
            if (departmentId) {
                body.department_id = Number(departmentId);
            }
            const res = await mutate<{ public_code: string; thread: ThreadPayload }>(
                '/admin/purchase-order-codes/from-minimal',
                token,
                'POST',
                body,
            );
            setLastPublicCode(res.public_code);
            setLastThread(res.thread);
            setResolvedThread(null);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Greška');
        } finally {
            setSaving(false);
        }
    }

    async function submitResolve(e: FormEvent) {
        e.preventDefault();
        setError('');
        try {
            const q = new URLSearchParams();
            q.set('public_code', resolveCode.trim());
            const res = await api<{ thread: ThreadPayload }>(`/admin/purchase-order-codes/resolve?${q.toString()}`, token);
            setResolvedThread(res.thread);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Greška');
        }
    }

    const t = {
        intro:
            locale === 'en'
                ? 'Minimal code (ULID) is stored in BI; full context is resolved by lookup. Person + project required; department if ambiguous.'
                : 'Minimalna šifra (ULID) sprema se u BI; puni kontekst dolazi lookupom. Obavezno osoba + projekt; odjel ako je višeznačno.',
        gen: locale === 'en' ? 'Generate code' : 'Generiraj šifru',
        resolveBtn: locale === 'en' ? 'Decode' : 'Razveži',
    };

    function threadBox(thread: ThreadPayload | null) {
        if (!thread) {
            return null;
        }
        const rows: Array<{ label: string; value: string | null }> = [
            { label: locale === 'en' ? 'Public code' : 'Javna šifra', value: thread.public_code },
            { label: locale === 'en' ? 'Company' : 'Tvrtka', value: thread.company ? `${thread.company.code} — ${thread.company.name}` : null },
            { label: locale === 'en' ? 'Project' : 'Projekt', value: thread.project ? `${thread.project.code} — ${thread.project.name}` : null },
            { label: locale === 'en' ? 'Sector' : 'Sektor', value: thread.sector ? `${thread.sector.code} — ${thread.sector.name_hr}` : null },
            { label: locale === 'en' ? 'Responsible person' : 'Osoba zaduženja', value: thread.responsible_person?.display_name ?? null },
            { label: locale === 'en' ? 'Department' : 'Odjel', value: thread.department ? `${thread.department.code} — ${thread.department.name}` : null },
            { label: locale === 'en' ? 'Channel' : 'Kanal', value: thread.department?.kanal ?? null },
            { label: locale === 'en' ? 'Sector' : 'Sektor', value: thread.sector ? `${thread.sector.code} — ${thread.sector.name_hr}` : null },
            { label: locale === 'en' ? 'Cost place' : 'Mjesto troška (MTP)', value: thread.cost_place_node ? `${thread.cost_place_node.code} — ${thread.cost_place_node.name_hr}` : null },
        ];
        return (
            <div className="thread-box" style={{
                marginTop: '1rem',
                padding: '0.85rem 1rem',
                background: 'rgba(var(--ad-acc-rgb), 0.06)',
                border: '1px solid rgba(var(--ad-acc-rgb), 0.25)',
                borderRadius: 10,
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                gap: '0.4rem 1rem',
                alignItems: 'baseline',
            }}>
                {rows.map((r) => r.value !== null ? (
                    <React.Fragment key={r.label}>
                        <span className="muted" style={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{r.label}</span>
                        <strong style={{ fontSize: '0.95rem' }}>{r.value}</strong>
                    </React.Fragment>
                ) : null)}
            </div>
        );
    }

    return (
        <section className="card admin-block">
            <div className="bi-module-hero">
                <div className="bi-module-hero-icon">🔢</div>
                <div className="bi-module-hero-text">
                    <h1>{title}</h1>
                    <p className="muted">{t.intro}</p>
                </div>
            </div>

            {error ? <p className="error">{error}</p> : null}
            {loading ? <p className="muted">{locale === 'en' ? 'Loading…' : 'Učitavam…'}</p> : null}

            {!loading && bootstrap ? (
                <>
                    {/* Kartica 1: Generiraj iz minimalnog unosa */}
                    <section className="admin-section-card" style={{ marginBottom: '1.25rem' }}>
                        <header style={{ marginBottom: '0.85rem' }}>
                            <h4 style={{ margin: 0 }}>{locale === 'en' ? 'Generate from minimal input' : '① Generiraj šifru'}</h4>
                            <p className="muted" style={{ margin: '0.2rem 0 0', fontSize: '0.88rem' }}>
                                {locale === 'en'
                                    ? 'Pick company, responsible person and project. Department is optional (auto if ambiguous).'
                                    : 'Odaberi tvrtku, osobu zaduženja i projekt. Odjel je opcijski (auto ako je nedvoznačno).'}
                            </p>
                        </header>
                        <form onSubmit={submitMinimal}>
                            <div style={{ marginBottom: '0.85rem' }}>
                                <QuickSearchInput
                                    value={pickerSearch}
                                    onChange={setPickerSearch}
                                    placeholder={locale === 'en' ? 'Filter persons / projects / departments…' : 'Filtriraj osobe / projekte / odjele…'}
                                />
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(16rem, 1fr))', gap: '0.85rem', maxWidth: '60rem' }}>
                                <label className="filter-label" style={{ margin: 0 }}>
                                    {locale === 'en' ? 'Company' : 'Tvrtka'}
                                    <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} required>
                                        <option value="">{locale === 'en' ? 'Select' : 'Odaberi'}</option>
                                        {companies.map((c) => (
                                            <option key={c.id} value={c.id}>{c.code} · {c.name}</option>
                                        ))}
                                    </select>
                                </label>
                                <label className="filter-label" style={{ margin: 0 }}>
                                    {locale === 'en' ? 'Responsible person' : 'Osoba zaduženja'}
                                    <select value={personId} onChange={(e) => setPersonId(e.target.value)} required>
                                        <option value="">{locale === 'en' ? 'Select' : 'Odaberi'}</option>
                                        {personsPick.map((p) => (
                                            <option key={p.id} value={p.id}>{p.display_name}</option>
                                        ))}
                                    </select>
                                </label>
                                <label className="filter-label" style={{ margin: 0 }}>
                                    {locale === 'en' ? 'Project' : 'Projekt'}
                                    <select value={projectId} onChange={(e) => setProjectId(e.target.value)} required>
                                        <option value="">{locale === 'en' ? 'Select' : 'Odaberi'}</option>
                                        {projectsPick.map((p) => (
                                            <option key={p.id} value={p.id}>{p.code} · {p.name}</option>
                                        ))}
                                    </select>
                                </label>
                                <label className="filter-label" style={{ margin: 0 }}>
                                    {locale === 'en' ? 'Sector' : 'Sektor'}
                                    <select value={sectorId} onChange={(e) => {
                                        const v = e.target.value;
                                        setSectorId(v);
                                        if (v && departmentId) {
                                            const d = bootstrap?.departments.find((x) => String(x.id) === departmentId);
                                            if (d && String(d.sector_id ?? '') !== v) setDepartmentId('');
                                        }
                                    }}>
                                        <option value="">{locale === 'en' ? '— all —' : '— svi —'}</option>
                                        {sectors.map((s) => (
                                            <option key={s.id} value={s.id}>{locale === 'en' ? s.name_en : s.name_hr}</option>
                                        ))}
                                    </select>
                                </label>
                                <label className="filter-label" style={{ margin: 0 }}>
                                    {locale === 'en' ? 'Channel' : 'Kanal'}
                                    <select value={kanalVal} onChange={(e) => {
                                        const v = e.target.value;
                                        setKanalVal(v);
                                        if (v && departmentId) {
                                            const d = bootstrap?.departments.find((x) => String(x.id) === departmentId);
                                            if (d && d.kanal !== v) setDepartmentId('');
                                        }
                                    }}>
                                        <option value="">{locale === 'en' ? '— all —' : '— svi —'}</option>
                                        {kanali.map((k) => (
                                            <option key={k} value={k}>{k}</option>
                                        ))}
                                    </select>
                                </label>
                                <label className="filter-label" style={{ margin: 0 }}>
                                    {locale === 'en' ? 'Department (if required)' : 'Odjel (ako treba)'}
                                    <select value={departmentId} onChange={(e) => {
                                        const v = e.target.value;
                                        setDepartmentId(v);
                                        const d = bootstrap?.departments.find((x) => String(x.id) === v);
                                        if (d && d.sector_id != null) setSectorId(String(d.sector_id));
                                        if (d && d.kanal) setKanalVal(d.kanal);
                                    }}>
                                        <option value="">{locale === 'en' ? '— auto —' : '— automatski —'}</option>
                                        {departmentsForProject.map((d) => (
                                            <option key={d.id} value={d.id}>{d.code} · {d.name}</option>
                                        ))}
                                    </select>
                                </label>
                            </div>
                            <div className="admin-toolbar" style={{ marginTop: '1rem' }}>
                                <button className="compact" type="submit" disabled={saving}>{t.gen}</button>
                            </div>
                            {lastPublicCode ? (
                                <p style={{ marginTop: '0.85rem' }}>
                                    <strong>{locale === 'en' ? 'public_code:' : 'Javna šifra:'}</strong>{' '}
                                    <code style={{ background: 'rgba(var(--ad-acc-rgb),0.12)', padding: '2px 8px', borderRadius: 6 }}>{lastPublicCode}</code>
                                </p>
                            ) : null}
                            {threadBox(lastThread)}
                        </form>
                    </section>

                    {/* Kartica 2: Razveži javnu šifru */}
                    <section className="admin-section-card">
                        <header style={{ marginBottom: '0.85rem' }}>
                            <h4 style={{ margin: 0 }}>{locale === 'en' ? '② Decode public_code' : '② Razveži javnu šifru'}</h4>
                            <p className="muted" style={{ margin: '0.2rem 0 0', fontSize: '0.88rem' }}>
                                {locale === 'en'
                                    ? 'Reverse lookup: paste a 26-character public_code to see the related person, project and department.'
                                    : 'Reverzni lookup: zalijepi 26-znakovni javni kod za prikaz osobe, projekta i odjela.'}
                            </p>
                        </header>
                        <form onSubmit={submitResolve}>
                            <label className="filter-label" style={{ display: 'block', maxWidth: '32rem', margin: 0 }}>
                                {locale === 'en' ? 'Public code' : 'Javna šifra'}
                                <input value={resolveCode} onChange={(e) => setResolveCode(e.target.value)} placeholder={locale === 'en' ? 'e.g. 11/1/017/0001' : 'npr. 11/1/017/0001'} autoFocus />
                            </label>
                            <div className="admin-toolbar" style={{ marginTop: '1rem' }}>
                                <button className="compact" type="submit">{t.resolveBtn}</button>
                            </div>
                            {threadBox(resolvedThread)}
                        </form>
                    </section>
                </>
            ) : null}
        </section>
    );
}
